import { CookieConsent, CONSENT_VERSION, CONSENT_EXPIRY_DAYS, LOCAL_STORAGE_KEY } from '@/types/cookies';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Get current cookie consent from localStorage
 */
export const getCookieConsent = (): CookieConsent | null => {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!stored) return null;
    
    const consent: CookieConsent = JSON.parse(stored);
    
    // Check if consent has expired
    const expiryDate = new Date(consent.timestamp + (CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000));
    if (new Date() > expiryDate) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      return null;
    }
    
    // Check if consent version is outdated
    if (consent.version !== CONSENT_VERSION) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      return null;
    }
    
    return consent;
  } catch (error) {
    console.warn('Failed to parse cookie consent:', error);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    return null;
  }
};

/**
 * Save cookie consent to localStorage
 */
export const setCookieConsent = (consent: Omit<CookieConsent, 'timestamp' | 'version'>): void => {
  if (typeof window === 'undefined') return;
  
  const fullConsent: CookieConsent = {
    ...consent,
    timestamp: Date.now(),
    version: CONSENT_VERSION
  };
  
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(fullConsent));
    
    // Trigger storage event for other tabs
    window.dispatchEvent(new Event('cookie-consent-updated'));
  } catch (error) {
    console.error('Failed to save cookie consent:', error);
  }
};

/**
 * Check if user has given consent for a specific category
 */
export const hasConsent = (category: keyof Omit<CookieConsent, 'timestamp' | 'version'>): boolean => {
  const consent = getCookieConsent();
  if (!consent) return false;
  
  return consent[category];
};

/**
 * Check if user has made any consent choice
 */
export const hasConsentChoice = (): boolean => {
  return getCookieConsent() !== null;
};

/**
 * Clear all consent data
 */
export const clearCookieConsent = (): void => {
  if (typeof window === 'undefined') return;
  
  localStorage.removeItem(LOCAL_STORAGE_KEY);
  window.dispatchEvent(new Event('cookie-consent-updated'));
};

/**
 * Get default consent (essential only)
 */
export const getDefaultConsent = (): Omit<CookieConsent, 'timestamp' | 'version'> => ({
  essential: true,
  analytics: false,
  marketing: false
});

/**
 * Get accept all consent
 */
export const getAcceptAllConsent = (): Omit<CookieConsent, 'timestamp' | 'version'> => ({
  essential: true,
  analytics: true,
  marketing: true
});

/**
 * Clean up cookies based on consent
 */
export const cleanupCookies = (consent: CookieConsent): void => {
  if (typeof window === 'undefined') return;
  
  // Clean up analytics cookies if not consented
  if (!consent.analytics) {
    const analyticsCookies = ['_ga', '_gid', '_gat', '_gat_gtag_UA_'];
    analyticsCookies.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    
    // Clear Google Analytics related data
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }
  }
  
  // Clean up marketing cookies if not consented
  if (!consent.marketing) {
    const marketingCookies = ['_fbp', '_fbc', 'fr'];
    marketingCookies.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  }
};
